import type { Ohaeng } from '../types/index';
import type { FourPillars, PillarPosition } from '../types/index';
import { getStemElement, getBranchElement } from '../core/five-elements';
import { getHiddenStemsWithRatio } from '../core/hidden-stems';

const ALL_POSITIONS: readonly PillarPosition[] = ['year', 'month', 'day', 'hour'];
const NO_HOUR_POSITIONS: readonly PillarPosition[] = ['year', 'month', 'day'];

function emptyDist(): Record<Ohaeng, number> {
  return { '木': 0, '火': 0, '土': 0, '金': 0, '水': 0 };
}

function getPositions(noHour?: boolean): readonly PillarPosition[] {
  return noHour ? NO_HOUR_POSITIONS : ALL_POSITIONS;
}

/**
 * 사주 네 기둥의 오행 분포를 계산한다 (천간 + 지지 + 지장간).
 *
 * 가중치:
 * - 천간: 각 1.0
 * - 지지: 각 1.0 (지지 자체의 대표 오행)
 * - 지장간: 비례 비율 (합계 1.0으로 정규화)
 */
export function calculateOhaengDistribution(fourPillars: FourPillars, noHour?: boolean): Record<Ohaeng, number> {
  const dist = emptyDist();
  const positions = getPositions(noHour);

  for (const pos of positions) {
    const pillar = fourPillars[pos];

    // 천간 오행
    dist[getStemElement(pillar.stem)] += 1;

    // 지지 오행 (지지 자체의 대표 오행)
    dist[getBranchElement(pillar.branch)] += 1;

    // 지장간 (지지 안에 숨어있는 천간들, 비례 가중치)
    const hiddenWithRatio = getHiddenStemsWithRatio(pillar.branch);
    const totalRatio = hiddenWithRatio.reduce((sum, h) => sum + h.ratio, 0);

    for (const { stem, ratio } of hiddenWithRatio) {
      const el = getStemElement(stem);
      dist[el] += ratio / totalRatio;
    }
  }

  // Round to 2 decimal places
  for (const key of Object.keys(dist) as Ohaeng[]) {
    dist[key] = Math.round(dist[key] * 100) / 100;
  }

  return dist;
}

/**
 * 천간 + 지지만의 오행 분포를 계산한다 (지장간 제외).
 *
 * 천간 4자 + 지지 4자 = 8자, 각 1.0으로 카운트.
 */
export function calculateOhaengDistributionSimple(fourPillars: FourPillars, noHour?: boolean): Record<Ohaeng, number> {
  const dist = emptyDist();
  const positions = getPositions(noHour);

  for (const pos of positions) {
    const pillar = fourPillars[pos];

    // 천간 오행
    dist[getStemElement(pillar.stem)] += 1;

    // 지지 오행
    dist[getBranchElement(pillar.branch)] += 1;
  }

  return dist;
}
